import { formatMoney, formatNumber, formatPoints, t, tx } from '../i18n';
import type { Metric } from '../types/analytics';

const seconds = (value: number | string) => t('common.unit.seconds', { value });
const minutes = (value: number) => t('common.unit.minutes', { value });
const hours = (value: number) => t('common.unit.hours', { value });

/** 45 -> "45 s", 130 -> "2 m 10 s" ("2 min 10 s" in Serbian), 3900 -> "1 h 5 m". */
export function formatDuration(totalSeconds: number): string {
  const total = Math.round(totalSeconds);
  if (total < 60) return seconds(total);
  if (total < 3600) {
    const m = Math.floor(total / 60); const s = total % 60;
    return s === 0 ? minutes(m) : `${minutes(m)} ${seconds(s)}`;
  }
  const h = Math.floor(total / 3600); const m = Math.floor((total % 3600) / 60);
  return m === 0 ? hours(h) : `${hours(h)} ${minutes(m)}`;
}

export function formatMetric(metric: Metric, currency = 'USD'): string {
  if (metric.availability !== 'available' || metric.value === null) return t('v1.metric.noData');
  if (metric.unit === 'percent') return `${formatNumber(metric.value)}%`;
  if (metric.unit === 'currency') return formatMoney(metric.value, currency, metric.value < 1 ? 3 : 2);
  if (metric.unit === 'milliseconds') return metric.value >= 1000 ? seconds(formatNumber(metric.value / 1000, 2, 2)) : t('common.unit.ms', { value: formatNumber(metric.value) });
  if (metric.unit === 'seconds') return formatDuration(metric.value);
  return formatNumber(metric.value);
}

export interface DeltaInfo { text: string; direction: 'up' | 'down' | 'flat' }

/** Change from `previous` to `current`, or null when either side is unavailable (a missing value is never read as zero). */
export function metricDelta(current: Metric, previous: Metric | undefined | null, currency = 'USD'): DeltaInfo | null {
  if (!previous) return null;
  if (current.availability !== 'available' || previous.availability !== 'available' || current.value === null || previous.value === null) return null;
  const change = current.value - previous.value;
  const direction = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
  const sign = change > 0 ? '+' : change < 0 ? '-' : '';
  const magnitude = { ...current, value: Math.abs(change) };
  const absolute = current.unit === 'percent' ? formatPoints(Math.abs(change)) : formatMetric(magnitude, currency);
  if (change === 0) return { text: t('common.metric.noChange'), direction };
  const relative = current.unit !== 'percent' && previous.value !== 0 ? ` (${sign}${formatNumber(Math.abs(change / previous.value) * 100)}%)` : '';
  return { text: `${sign}${absolute}${relative}`, direction };
}

export function MetricCard({ label, labelTitle, metric, helper, currency, previous, compareLabel, lowerIsBetter }: {
  label: string;
  /** Explanation of an abbreviation in the label (DAU, MAU), shown on hover. */
  labelTitle?: string; metric: Metric; helper?: string; currency?: string;
  /** The same metric for the comparison range. Shown as a change only when both values exist. */
  previous?: Metric | null; compareLabel?: string; lowerIsBetter?: boolean;
}) {
  const delta = metricDelta(metric, previous, currency);
  const unavailable = metric.availability !== 'available';
  const tone = delta && delta.direction !== 'flat' ? ((delta.direction === 'up') !== Boolean(lowerIsBetter) ? 'good' : 'bad') : 'neutral';
  return (
    <article className={`metric-card ${unavailable ? 'unavailable' : ''}`}>
      <span title={labelTitle}>{label}</span>
      <strong>{formatMetric(metric, currency)}</strong>
      {delta && <em className={`delta ${delta.direction} ${tone}`} aria-label={t('v1.metric.change', { compare: compareLabel ?? '', delta: delta.text })}>{delta.text}{compareLabel ? ` ${compareLabel}` : ''}</em>}
      <small>{helper || (unavailable ? tx(metric.reason) ?? (metric.kind === 'estimated' ? t('v1.metric.estimatedUnavailable') : t('common.unavailable')) : metric.kind === 'estimated' ? t('common.estimated') : metric.kind === 'mock' ? t('v1.metric.mock') : t('common.actual'))}</small>
    </article>
  );
}
